import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import OptimizedImage from './OptimizedImage';
import { preloadImages } from '../utils/imageOptimization';

interface CarouselProps {
  images: string[]; // Array of image URLs
  autoPlay?: boolean;
  interval?: number;
  showDots?: boolean;
}

const Carousel: React.FC<CarouselProps> = ({ 
  images, 
  autoPlay = true, 
  interval = 4000,
  showDots = false 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState<boolean[]>(new Array(images.length).fill(false));
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Preload all carousel images for smooth transitions
  useEffect(() => {
    preloadImages(images);
  }, [images]);

  // Automatically cycle through images
  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [images.length, autoPlay, interval]);

  // Pause carousel on hover
  const handleMouseEnter = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (autoPlay && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, interval);
    }
  };

  const handleImageLoad = (index: number) => {
    setIsLoaded(prev => {
      const newLoaded = [...prev];
      newLoaded[index] = true;
      return newLoaded;
    });
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (images.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: '300px', sm: '400px', md: '500px' },
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        No images available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: '300px', sm: '400px', md: '500px' },
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: 0, md: '8px' },
        boxShadow: { xs: 'none', md: '0 4px 20px rgba(0,0,0,0.1)' },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="region"
      aria-label="Image carousel showcasing HDF Hauling services"
    >
      {images.map((image, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: currentIndex === index ? 1 : 0,
            transition: 'opacity 0.8s ease-in-out',
            zIndex: currentIndex === index ? 2 : 1,
          }}
        >
          <img
            src={image}
            alt={`HDF Hauling service showcase ${index + 1} - Professional trucking and fleet management`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
            }}
            onLoad={() => handleImageLoad(index)}
          />
        </Box>
      ))}

      {/* Navigation dots */}
      {showDots && images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 3,
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              component="button"
              onClick={() => goToSlide(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: 'none',
                backgroundColor: currentIndex === index ? 'white' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'white',
                  transform: 'scale(1.1)',
                },
                '&:focus': {
                  outline: '2px solid white',
                  outlineOffset: '2px',
                },
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </Box>
      )}

      {/* Loading indicator for first image */}
      {!isLoaded[currentIndex] && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: 2,
            borderRadius: 1,
          }}
        >
          Loading...
        </Box>
      )}
    </Box>
  );
};

export default Carousel;